import Image from 'next/image';
import React from 'react';
import Link from 'components/common/link';
import { BlogPostContentType } from 'constants/blog';
import {
  BLOG_TOP10APPS,
  BLOG_TOP10APPS_APPLECALENDAR,
  BLOG_TOP10APPS_AUDIBLE,
  BLOG_TOP10APPS_GOODNOTES,
  BLOG_TOP10APPS_HEADSPACE,
  BLOG_TOP10APPS_KINDLE,
  BLOG_TOP10APPS_NOTION,
  BLOG_TOP10APPS_SLEEPCYCLE,
  BLOG_TOP10APPS_SPOTIFY,
  BLOG_TOP10APPS_TODOIST,
  BLOG_TOP10APPS_TOGGL,
} from 'constants/images';
import { BlogPost } from 'containers/blog/post/types';
import { theme } from 'utils/theme';

const LOGO_SIZE_PX = 30;
const LOGO_MARGIN_RIGHT = '10px';

const Top10Apps: BlogPost = {
  id: '10apps',
  title: '10 apps for better days',
  subtitle: 'The list I wish I had when I started',
  previewImage: BLOG_TOP10APPS,
  date: new Date('23 Sep 2022'),
  content: [
    {
      type: BlogPostContentType.TITLE,
      content: '10 apps for better days',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: 'The list I wish I had when I started',
    },
    {
      type: BlogPostContentType.POST_INFO,
      content: '23 Sep 2022 · 8 minute read',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I've done a bit of self-reflection and it seems I'm using more or less the same 10 to 15 apps weekly. Compared to a few years ago, this is quite some intense use of modern-day technology, so I wanted to break down the top 10 and see how each provides value to my days. Let's start with what matters most. Sleep.",
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_TOP10APPS,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Sleep Cycle"
          src={BLOG_TOP10APPS_SLEEPCYCLE}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Sleep cycle',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I've always been highly influenced by sleep, being used to get 9+ hours in high school while doing a lot of sports. Since uni started, my sleep schedule got a bit hectic, with not only short nights but also inconsistent bedtime and wake-up times. I've learned this lesson the hard way after things got out of my control somewhere in the 2nd year of university, so I needed something to keep me accountable and organized. First, I implemented a simple 10-6 sleep schedule, but after a few months of doing this, I wanted something to quantify the quality as well.",
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'Sleep quantity is important, but far from the importance of quality. REM sleep is a big deal and matching your wake-up times to your sleep cycles, which last more or less 90 minutes, is crucial to waking up rested. As Matthew Walker explains in ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/gp/product/1501144324/ref=as_li_qf_asin_il_tl?ie=UTF8&tag=antonmihai-20&creative=9325&linkCode=as2&creativeASIN=1501144324&linkId=cfcbf0bd1c9fb915ba4d6c577b69a19a"
        >
          Why We Sleep
        </Link>,
        ", that's the reason why after some 6 hour nights you sometimes feel way more rested than after sleeping in 9+ hours. You maybe just got lucky and your wake-up time matched the end of one sleep cycle, which is no more than a period of deep sleep followed by a shallow sleep 'break'.",
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'I use ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.sleepcycle.com/"
        >
          Sleep Cycle
        </Link>,
        " every night to track my sleep quality, REM sleep occurrence, and level of movement, measured with my phone's microphone. What's even cooler, this app lets you specify the desired time when you want to be up and a wake-up window, and it will do its best to wake you up optimally in that interval when your sleep is the shallowest.",
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Apple Calendar"
          src={BLOG_TOP10APPS_APPLECALENDAR}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Apple Calendar',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "A calendar, I know. And a basic one. Contrary to the fact that it's a super simple tool, I was greatly underestimating it before I became a power user. Especially in periods of high workload, I have to keep my mind free for creative work, not for remembering stuff. So, I offload everything in the calendar, which has become more or less the cookbook for my days. I start by marking the most basic things, sleep, eating and gym. Then, I schedule work, study, and leisure activities. The idea is everything that gets in there is a priority. I usually plan the week to come on Sundays and try to follow the schedule as close as possible. As Parkinson's law goes, work expands the time you have for it, thus I try to schedule fixed time blocks of focused time to get things done efficiently. This proactive approach works since I get to schedule the week at a time when I'm not under pressure, so when I am I know exactly what I have to do.",
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Todoist"
          src={BLOG_TOP10APPS_TODOIST}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Todoist',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'Getting my calendar sharp and organized sets the big picture of what I spend my time on, but each activity has smaller tasks to handle. Writing those to the calendar would make it a mess, so I use ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://todoist.com/app/"
        >
          Todoist
        </Link>,
        " to 'micromanage' activities. I have it split into Work, School, and Personal, like anything else, and I add any small task that comes to mind and tag it accordingly. Sometimes ideas come in the most unexpected places, while doing the groceries, while running, or at the gym, so dumping them to Todoist helps to remember the volatile.",
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Headspace"
          src={BLOG_TOP10APPS_HEADSPACE}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Headspace',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'Switching the area a bit, keeping things organized with calendars and lists works great, but another thing that has to be kept clean is our minds. Mental health is a big deal, especially if you try to get a lot of things done. I started experimenting with this kind of technique in the 3rd year of uni and right now I have been consistently doing this for one year. There are a few apps to pick from, but ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.headspace.com/"
        >
          Headspace
        </Link>,
        ' just kept me engaged. They have a lot of packs and topics to choose from and they iteratively go from simple to advanced methods. I was not expecting this, but 15 minutes in the morning do wonders on the clarity of mind over the day.',
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Notion"
          src={BLOG_TOP10APPS_NOTION}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Notion',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: `To be honest, keeping notes on paper never scaled how I wanted to. They would either get lost, thrown away, or too many to keep track of. Digital notes are a no-brainer, wonder why it took so long to transition to them.`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'I started taking digital notes a few years ago. Heard about this ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.notion.so/"
        >
          Notion
        </Link>,
        " app from a guy on youtube, so I decided to give it a shot. I loved it. It's so flexible that you can do a lot of things. You can keep track of your life, write journals, set up todo lists, or take school notes. I'm doing all of those, but I find it extremely useful for school notes. I started using it in the first year of my Master's for this and it's a perfect tool. It allows you to structure the content however you want, embed a lot of different 3rd party components and, probably few people care about this, write native Latex, which allows writing fancy math.",
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Goodnotes 5"
          src={BLOG_TOP10APPS_GOODNOTES}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Goodnotes 5',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'For most of my life (before Notion at least) I have taken handwritten notes on paper, this being the obvious default in school. Not a long time ago I thought of trying out writing on the iPad to see how this scales. It was weird at first, the glide was different, friction was close to none, but it got better with time. I first took digital hand notes while studying for my Master’s exam in the first year and I loved it. There are many apps on the market, offering different types of experiences, but I stuck with ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.goodnotes.com/"
        >
          GoodNotes 5
        </Link>,
        ' and so far, so good. You can take the basic approach and create notebooks for everything you need, with different predefined templates, pen colors, and styles, or you can define your custom structure for optimal note-taking.',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'What I found this very interesting, although you can probably do it in a way or another in other apps as well, is read papers and articles. I did this a lot using the app and it worked great for me. When analyzing a paper, you often need to highlight and take notes on certain parts. GoodNotes allowed me to import pdfs, read them normally and add flashcards near the sections where I needed more clarification.',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Audible"
          src={BLOG_TOP10APPS_AUDIBLE}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Audible',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'Switching the area from productivity to media consumption, ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/hz/audible/mlp/membership/plus?ref_=assoc_tag_ph_1524216631897&_encoding=UTF8&camp=1789&creative=9325&linkCode=pf4&tag=antonmihai-20&linkId=63677c7520e06e262aeb76ed60cdae41"
        >
          Audible
        </Link>,
        ' is the to-go place when I want to consume a book. You can buy and read the physical book, as people did for ages, but there’s a catch to this. You cannot read and drive, read and run or read and do anything else that can be done out of muscle memory. With audiobooks, I can do all of this. When I’m driving home for 6 hours, I listen to one book. When I go to the gym in the morning, I listen to one hour of a book. This works great for me. Workouts are often repetitive and don’t require much thinking, so listening to a book makes me double productive. That feels good.',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'The all-time favorite is ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/gp/product/0306924633/ref=as_li_qf_asin_il_tl?ie=UTF8&tag=antonmihai-20&creative=9325&linkCode=as2&creativeASIN=0306924633&linkId=6c95fb2479b42d3931114c7b68d9b05f"
        >
          The Compound Effect
        </Link>,
        ', which I’ve listened to 3 times so far, but there are a lot of good listens out there, such as ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/gp/product/1501144324/ref=as_li_tl?ie=UTF8&tag=antonmihai-20&camp=1789&creative=9325&linkCode=as2&creativeASIN=1501144324&linkId=4e364acd11f19c71ec02720d78b94118"
        >
          Why We Sleep
        </Link>,
        ', ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/gp/product/081297381X/ref=as_li_tl?ie=UTF8&tag=antonmihai-20&camp=1789&creative=9325&linkCode=as2&creativeASIN=081297381X&linkId=25df1c49c6f3e189dc0529719b23bff6"
        >
          The Black Swan
        </Link>,

        ', and ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/gp/product/B007H9EVM0/ref=as_li_tl?ie=UTF8&tag=antonmihai-20&camp=1789&creative=9325&linkCode=as2&creativeASIN=B007H9EVM0&linkId=f0c22446c3b4409e29dd6211dcccf7ed"
        >
          The Diary of A Young Girl
        </Link>,
        '.',
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Spotify"
          src={BLOG_TOP10APPS_SPOTIFY}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Spotify',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'Spotify does with music and podcasts what Audible does with books. It’s a simple product but adds a lot of value to my days. It has quality music, good recommendations and it pairs with Alexa for better hands-off interaction. When I don’t listen to an audiobook while working out, I’m listening to a podcast. Spotify offers a wide variety of podcasts, and, since they are less packed with information compared to an audiobook, listening to them at 2X speed brings a quite good return on investment looking at the things I learn.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'The podcasts I’ve heavily listened to for the last few weeks are the StartUp Podcast and Stanford GSB: View From The Top. It’s very inspiring to hear successful people talking about the ups and downs of starting a business and the journey they went on while doing so.',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Kindle"
          src={BLOG_TOP10APPS_KINDLE}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Kindle',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        "I mostly consume books in physical or audio format, but sometimes it's nice to have some 3rd alternative in your backpack, that does not consume any extra space. I use the kindle app when I want to read something on a screen. Bundled with a screen protector that makes reading less straining for the eyes provides a nice experience. Also, a productive flow I sometimes use is to split the screen having kindle and notion at the same time, so you can read and take notes without switching apps. The current in-progress book on kindle is ",
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://www.amazon.com/gp/product/0525572422/ref=as_li_qf_asin_il_tl?ie=UTF8&tag=antonmihai-20&creative=9325&linkCode=as2&creativeASIN=0525572422&linkId=016fbb7a68e833f5e3e19dcd0e094ca6"
        >
          Make Time
        </Link>,
        ", a book by 2 Google employees that talk about making the most of your day, rethinking what the word 'busyness' means.",
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: [
        <Image
          key="image"
          width={LOGO_SIZE_PX}
          height={LOGO_SIZE_PX}
          sizes="100vw"
          alt="Toggl track"
          src={BLOG_TOP10APPS_TOGGL}
          style={{ marginRight: LOGO_MARGIN_RIGHT }}
        />,
        'Toggl track',
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        'All those 9 apps so far take quite some time to use and consume content with, so there must be a way to track how each contributes to my life. For this, I use ',
        <Link
          key="link"
          target="_blank"
          rel="noopener noreferrer"
          color={theme.customColors.linkBlue}
          href="https://toggl.com/track/"
        >
          toggl track
        </Link>,
        ", which delivers a lot of value given its compact functionality. It's a time tracking app that allows you to do a basic thing: see what or who takes your time away. I usually track only work, school, and self-growth related activities, which average around 70 hours a week, but I also have friends that track every minute of their lives. That's cool. And probably takes a lot of time as well.",
      ],
    },
    {
      type: BlogPostContentType.SECTION_BREAK,
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "Those are the 10 apps and systems that most influenced my life in the last few years. I'll probably add more to the list, but for sure the functionalities described above will stick for a few years, keeping me productive, mindful, and entertained. Give them a shot!",
    },
  ],
};

export default Top10Apps;
